import { useGridFilter } from '@mint-lib/ag-grid/react';
import { ChangeEvent, useCallback, useMemo, useState, type FC } from 'react';

import type {
  IAfterGuiAttachedParams,
  IDoesFilterPassParams,
} from '@mint-lib/ag-grid/enterprise';
import { useDateFormatter } from '@mint-lib/formatting-hooks';
import {
  CalendarRange,
  DatepickerInput,
  DatepickerInputRoot,
  type DateRange,
} from '@mint-lib/tw-components/patterns';

import useDateRangePicker from '../../../hooks/useDateRangeFilter.js';
import type { ApprovalTableItemData } from '../../../types/approversTable.js';
import { DateRangeFilterProps } from '../../../types/filters.js';
import {
  doesValuePassFilter,
  getModelFromRange,
  getRangeFromModel,
  isDateRangeModelsEqual,
} from '../../../utils/filters.js';

const DateRangeFilter: FC<DateRangeFilterProps> = ({
  model,
  onModelChange,
  onUiChange,
  getValue,
}) => {
  const [containerType, setContainerType] = useState<
    'columnFilter' | 'toolPanel' | null
  >(null);
  const [isOpened, setIsOpened] = useState(false);

  const {
    selectedRange,
    selectedMonth,
    changeRange,
    changeMonth,
    resetLocalState,
    syncLocalStateWithModel,
  } = useDateRangePicker(model);

  const afterGuiAttached = useCallback(
    (params: IAfterGuiAttachedParams) => {
      if (!params?.container) {
        return;
      }

      if (params.container === 'columnFilter') {
        syncLocalStateWithModel();
        setContainerType(params.container);
      }

      if (params.container === 'toolPanel') {
        setContainerType(params.container);
      }
    },
    [syncLocalStateWithModel],
  );

  const afterGuiDetached = useCallback(() => {
    if (containerType === 'columnFilter') {
      resetLocalState();
    }
    setIsOpened(false);
  }, [containerType, resetLocalState]);

  const doesFilterPass = useCallback(
    ({ node }: IDoesFilterPassParams<ApprovalTableItemData>) => {
      const value = getValue<string>(node);

      return doesValuePassFilter(model, value);
    },
    [model, getValue],
  );

  useGridFilter({
    doesFilterPass,
    afterGuiAttached,
    afterGuiDetached,
  });

  const rangeFromModel = useMemo(() => {
    return getRangeFromModel(model);
  }, [model]);

  const { formattedDateRange } = useDateFormatter(
    rangeFromModel.from,
    rangeFromModel.to,
  );

  const hidePopup = () => {
    const escapeKeydownEvent = new KeyboardEvent('keydown', {
      key: 'Escape',
      bubbles: true,
    });
    document.dispatchEvent(escapeKeydownEvent);
  };

  const handleColumnFilterApply = (dateRange?: DateRange) => {
    const filterModel = getModelFromRange(dateRange);

    if (isDateRangeModelsEqual(filterModel, model)) {
      hidePopup();
      return;
    }

    onUiChange();
    onModelChange(filterModel);
    hidePopup();
  };

  const handleToolpanelFilterApply = (dateRange?: DateRange) => {
    const filterModel = getModelFromRange(dateRange);

    if (isDateRangeModelsEqual(filterModel, model)) {
      setIsOpened(false);
      return;
    }

    onUiChange();
    onModelChange(filterModel);
    setIsOpened(false);
  };

  const handleColumnFilterReset = () => {
    if (!model) {
      return;
    }

    resetLocalState();
    onUiChange();
    onModelChange(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      syncLocalStateWithModel();
      setIsOpened(true);
      return;
    }

    resetLocalState();
    setIsOpened(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      resetLocalState();
      onUiChange();
      onModelChange(null);
    }
  };

  if (!containerType) {
    return 'Loading...';
  }

  if (containerType === 'columnFilter') {
    return (
      <CalendarRange
        aria-label="date range filter"
        selected={selectedRange}
        month={selectedMonth}
        onMonthChange={changeMonth}
        onApply={handleColumnFilterApply}
        onReset={handleColumnFilterReset}
        onSelect={changeRange}
      />
    );
  }

  return (
    <DatepickerInputRoot className="px-3">
      <DatepickerInput
        fullWidth
        size="small"
        variant="range"
        align="end"
        value={formattedDateRange}
        open={isOpened}
        onOpenChange={handleOpenChange}
        onChangeInput={handleInputChange}
        popoverClassName="z-popover"
        isPortal
      >
        <CalendarRange
          month={selectedMonth}
          onMonthChange={changeMonth}
          selected={selectedRange}
          onReset={resetLocalState}
          onSelect={changeRange}
          onApply={handleToolpanelFilterApply}
        />
      </DatepickerInput>
    </DatepickerInputRoot>
  );
};

export default DateRangeFilter;
